<script setup>

// import { useGenerateImageVariant } from '@/@core/composable/useGenerateImageVariant'
// 🔹 useGenerateImageVariant selezionava automaticamente immagini diverse per tema chiaro/scuro

// import AuthProvider from '@/views/pages/authentication/AuthProvider.vue'
// 🔹 AuthProvider mostrava i pulsanti per login con provider esterni (es. Google, Facebook)

import { VNodeRenderer } from '@layouts/components/VNodeRenderer'

// 🔹 VNodeRenderer serviva per renderizzare loghi dinamici (es. SVG o componenti Vue invece di immagini statiche)

import { useAuthStore } from '@/stores/auth'
import { resolveRoleRedirect } from '@/utils/roleRoutes'
import { normalizeRole } from '@/utils/roles'
import { themeConfig } from '@themeConfig'
import { storeToRefs } from 'pinia'
import { nextTick, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'

definePage({
  meta: {
    layout: 'blank',
    public: true,
  },
})

const form = ref({
  email: '',
  password: '',
  remember: false,
})

const isPasswordVisible = ref(false)
const loginForm = ref(null)
const router = useRouter()
const authStore = useAuthStore()
const { loading: authLoading, error: authError, userRole } = storeToRefs(authStore)

const normalizeAuthError = rawError => {
  const message = typeof rawError === 'string' ? rawError : rawError?.message || ''
  if (!message)
    return null

  const normalizedMessage = message.toLowerCase()

  // Invece di 'validation'
  if (normalizedMessage.includes('validation'))
    return 'Please check that your email and password are correct.'
  if (normalizedMessage.includes('network') || normalizedMessage.includes('fetch'))
    return 'Connection error. Please check your internet connection.'

  return message.length > 100 ? 'An error occurred during login.' : message
}

const displayedErrorMessage = ref(null)
const invitationAccepted = ref(false)

onMounted(() => {
  invitationAccepted.value = router.currentRoute.value.query?.invitationAccepted === '1'
})

const resetAuthError = () => {
  if (displayedErrorMessage.value)
    displayedErrorMessage.value = null
  if (authError.value)
    authStore.clearError()
}

watch(authError, newValue => {
  displayedErrorMessage.value = normalizeAuthError(newValue)
}, { immediate: true })

// Funzione per pulire gli errori quando l'utente inizia a digitare
const clearErrorOnInput = () => {
  resetAuthError()
}

const isRedirectAllowed = (targetPath, role) => {
  if (typeof targetPath !== 'string' || !targetPath || !role)
    return false

  try {
    const resolved = router.resolve(targetPath)

    if (!resolved?.matched?.length)
      return false

    const isPublic = resolved.matched.some(record => record.meta?.public)
    if (isPublic)
      return true

    const requiredRoles = resolved.matched
      .map(record => record.meta?.roles)
      .flat()
      .filter(Boolean)
      .map(normalizeRole)

    if (!requiredRoles.length)
      return true

    const normalizedRole = normalizeRole(role)

    return requiredRoles.includes(normalizedRole)
  }
  catch (error) {
    console.warn('Unable to resolve redirect target', targetPath, error)

    return false
  }
}

const handleLogin = async () => {
  resetAuthError()

  if (!loginForm.value) return
  const validationResult = (await loginForm.value.validate())

  if (!validationResult.valid) return

  try {
    const response = await authStore.login(form.value)

    await nextTick()

    let role = response?.userData?.role || response?.role || (userRole && userRole.value)
    if (!role) {
      try {
        const me = await authStore.fetchAuthenticatedUser()

        role = me?.role || (userRole && userRole.value)
      }
      catch (fetchUserError) {
        console.error('Unable to resolve user role after login', fetchUserError)
      }
    }

    const redirectTarget = router.currentRoute.value.query?.redirect

    const safeRedirect = typeof redirectTarget === 'string' && !redirectTarget.startsWith('/login')
      ? redirectTarget
      : null

    const target = safeRedirect && isRedirectAllowed(safeRedirect, role)
      ? safeRedirect
      : resolveRoleRedirect(role)

    await router.push(target)
  }
  catch (error) {
    console.error('Login failed', error)

    // displayedErrorMessage.value = normalizeAuthError(authError.value || error?.response?._data?.message || error?.message || error)

    // The error is handled by the auth store, which updates authError
  }
}

// const authV2LoginMask = useGenerateImageVariant(authV2LoginMaskLight, authV2LoginMaskDark)
// const authV2LoginIllustration = useGenerateImageVariant(authV2LoginIllustrationLight, authV2LoginIllustrationDark, authV2LoginIllustrationBorderedLight, authV2LoginIllustrationBorderedDark, true)
// 🔹 Queste due costanti sceglievano automaticamente l'immagine giusta in base al tema attivo
</script>


<template>
  <a href="javascript:void(0)">
    <div class="app-logo auth-logo">
      <VNodeRenderer :nodes="themeConfig.app.logo" />
      <h1 class="app-logo-title">
        {{ themeConfig.app.title }}
      </h1>
    </div>
  </a>

  <VRow
    no-gutters
    class="auth-wrapper"
  >
    <VCol
      md="8"
      class="d-none d-md-flex align-center justify-center position-relative"
    >
      <div class="d-flex align-center justify-center pa-10">
        <!-- <img :src="authV2LoginIllustration" class="auth-illustration w-100" alt="auth-illustration" /> -->
        <!-- 🔹 Mostrava l'illustrazione principale, variabile in base al tema -->
      </div>
      <!-- <VImg :src="authV2LoginMask" class="d-none d-md-flex auth-footer-mask" alt="auth-mask" /> -->
      <!-- 🔹 Mostrava una maschera decorativa in fondo alla colonna sinistra -->
    </VCol>

    <VCol
      cols="12"
      md="4"
      class="auth-card-v2 d-flex align-center justify-center"
      style="background-color: rgb(var(--v-theme-surface));"
    >
      <VCard
        flat
        :max-width="500"
        class="mt-12 mt-sm-0 pa-5 pa-lg-7"
      >
        <VCardText>
          <h4 class="text-h4 mb-1">
            Welcome to <span class="text-capitalize">{{ themeConfig.app.title }}! 👋🏻</span>
          </h4>
          <p class="mb-0">
            Please sign-in to your account and start the adventure
          </p>
        </VCardText>

        <VCardText>
          <VAlert
            v-if="invitationAccepted"
            type="success"
            class="mb-4"
          >
            Account activated successfully. You can now sign in.
          </VAlert>
          <VForm
            ref="loginForm"
            @submit.prevent="handleLogin"
          >
            <VRow>
              <VCol cols="12">
                <VTextField
                  v-model="form.email"
                  autofocus
                  label="Email"
                  type="email"
                  placeholder="johndoe@email.com"
                  data-test-id="login-email"
                  @input="clearErrorOnInput"
                />
              </VCol>

              <VCol cols="12">
                <VTextField
                  v-model="form.password"
                  label="Password"
                  placeholder="············"
                  :type="isPasswordVisible ? 'text' : 'password'"
                  autocomplete="password"
                  :append-inner-icon="isPasswordVisible ? 'ri-eye-off-line' : 'ri-eye-line'"
                  data-test-id="login-password"
                  @click:append-inner="isPasswordVisible = !isPasswordVisible"
                  @input="clearErrorOnInput"
                />

                <div class="d-flex align-center justify-space-between flex-wrap my-6 gap-x-2">
                  <VCheckbox
                    v-model="form.remember"
                    label="Remember me"
                  />
                  <a
                    class="text-primary"
                    href="javascript:void(0)"
                  >Forgot Password?</a>
                </div>

                <VBtn
                  block
                  type="submit"
                  :loading="authLoading"
                  :disabled="authLoading"
                  data-test-id="login-submit"
                >
                  Login
                </VBtn>
                <VAlert
                  v-if="displayedErrorMessage"
                  type="error"
                  variant="tonal"
                  class="mt-4"
                  closable
                  @click:close="resetAuthError"
                >
                  {{ displayedErrorMessage }}
                </VAlert>
              </VCol>



              <VCol
                cols="12"
                class="d-flex align-center"
              >
                <VDivider />
                <span class="mx-4 text-high-emphasis">or</span>
                <VDivider />
              </VCol>

              <VCol
                cols="12"
                class="text-body-1 text-center pa-0 ma-0"
              >
                <span class="d-inline-block">New on our platform?</span>
                <br>
                <h3 class="text-h4 text-primary ms-1 d-inline-block pa-0">
                  Create an account
                </h3>
              </VCol>

          
              <!-- BOX DI REGISTRAZIONE -->

              <VRow
                class="my-6"
                justify="center"
                align="stretch"
                dense
              >
                <!-- Surgeon Secretary Box -->
                <VCol cols="12">
                  <VCard
                    class="pa-4 text-center"
                    outlined
                    rounded="lg"
                  >
                    <VCardTitle class="justify-center">
                      <VIcon
                        color="primary"
                        class="me-2"
                      >
                        ri-user-settings-line
                      </VIcon>
                      <span class="text-h6">Surgeon's Secretary</span>
                    </VCardTitle>

                    <VCardText>
                      Manage surgeon appointments and schedules.
                    </VCardText>

                    <VCardActions class="justify-center">
                      <VBtn
                        color="primary"
                        :to="{ name: 'users-register-surgeon-secretary' }"
                      >
                        Create your account
                      </VBtn>
                    </VCardActions>
                  </VCard>
                </VCol>

                <!-- Anesthetist Box -->
                <VCol cols="12">
                  <VCard
                    class="pa-4 text-center"
                    outlined
                    rounded="lg"
                  >
                    <VCardTitle class="justify-center">
                      <VIcon
                        color="primary"
                        class="me-2"
                      >
                        ri-heart-pulse-line
                      </VIcon>
                      <span class="text-h6">Anesthetist</span>
                    </VCardTitle>

                    <VCardText>
                      Support surgical teams with sedation.
                    </VCardText>

                    <VCardActions class="justify-center">
                      <VBtn
                   
                        color="primary"
                        :to="{ name: 'users-register-anaesthetist' }"
                      >
                        Create your account
                      </VBtn>
                    </VCardActions>
                  </VCard>
                </VCol>
              </VRow>

              <!-- BOX ISCRIZIONE FINE  -->
   

              
              <!-- 
                <VCol
                cols="12"
                class="text-center"
                > 
              -->

              <!-- <AuthProvider /> -->
              <!-- 🔹 Mostrava i pulsanti per login con provider esterni (Google, Facebook, ecc.) -->
              <!-- </VCol> -->
            </VRow>
          </VForm>
        </VCardText>
      </VCard>
    </VCol>
  </VRow>
</template>


<style lang="scss">
@use "@core-scss/template/pages/page-auth";
</style>
